/* Signup popup controller
 *
 * Reads its config from data-* attributes on the popup root so the
 * theme editor (sections/cnd-signup-popup.liquid) is the single
 * source of truth for trigger / delay / dismissal behaviour.
 *
 * Behaviours:
 *   - "timer" trigger: shows after data-delay ms of dwell time.
 *   - "exit" trigger:  shows when the cursor leaves the top of the
 *                       viewport (mouse leaves the document through
 *                       its top edge). Filters out internal mouse
 *                       travel between sibling elements.
 *   - "both" trigger:  first to fire wins (default).
 *   - Once-per-session via sessionStorage so a returning tab doesn't
 *     re-pop the same session.
 *   - A11y: focus trap inside the dialog, Esc closes, focus restored
 *     to the opener on close, body scroll locked while open.
 *   - On form success inside the dialog: if `data-success-redirect`
 *     is true, push the customer to /cart?discount=<data-discount-code>
 *     so checkout opens with the code applied.
 */
(function () {
  function bootstrap() {
    var popups = document.querySelectorAll("[data-signup-popup]");
    popups.forEach(initPopup);
  }

  function initPopup(popup) {
    if (popup.__cndPopupInit) return;
    popup.__cndPopupInit = true;

    var trigger = popup.getAttribute("data-trigger") || "both";
    var delay = parseInt(popup.getAttribute("data-delay"), 10) || 0;
    var oncePerSession = popup.getAttribute("data-once-per-session") !== "false";
    var successRedirect = popup.getAttribute("data-success-redirect") === "true";
    var discountCode = popup.getAttribute("data-discount-code") || "WELCOME10";

    if (oncePerSession && sessionStorage.getItem("cnd_signup_popup_seen") === "1") {
      // Already shown (or dismissed) in this session — bail.
      handleSuccessRedirectIfNeeded(popup, successRedirect, discountCode);
      return;
    }

    var opener = null;
    var shown = false;

    var dialog = popup.querySelector("[role='dialog']");
    var dismissers = popup.querySelectorAll("[data-signup-popup-dismiss]");
    var firstFocusable = null;
    var lastFocusable = null;

    function open() {
      if (shown) return;
      shown = true;
      opener = document.activeElement;
      popup.hidden = false;
      popup.setAttribute("aria-hidden", "false");
      document.body.classList.add("signup-popup-open");

      // Cache the focusable list once open. Use the dialog scope so
      // we can trap Tab inside it.
      var allFocusable = dialog.querySelectorAll(
        "a[href], button:not([disabled]), input:not([disabled]):not([type='hidden']), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex='-1'])"
      );
      firstFocusable = allFocusable[0] || dialog;
      lastFocusable = allFocusable[allFocusable.length - 1] || dialog;

      // Move focus into the dialog after the transition has started.
      requestAnimationFrame(function () {
        firstFocusable.focus();
      });

      if (oncePerSession) {
        try {
          sessionStorage.setItem("cnd_signup_popup_seen", "1");
        } catch (e) {
          /* sessionStorage unavailable — silently ignore */
        }
      }
    }

    function close() {
      if (popup.hidden) return;
      popup.hidden = true;
      popup.setAttribute("aria-hidden", "true");
      document.body.classList.remove("signup-popup-open");
      if (opener && typeof opener.focus === "function") {
        opener.focus();
      }
    }

    function onKeydown(e) {
      if (e.key === "Escape") {
        e.preventDefault();
        close();
        return;
      }
      if (e.key !== "Tab" || popup.hidden) return;
      // Focus trap: pull focus to the first focusable if it has
      // escaped past the last; send it back if shifted off the first.
      if (e.shiftKey && document.activeElement === firstFocusable) {
        e.preventDefault();
        lastFocusable.focus();
      } else if (!e.shiftKey && document.activeElement === lastFocusable) {
        e.preventDefault();
        firstFocusable.focus();
      }
    }

    dismissers.forEach(function (el) {
      el.addEventListener("click", close);
    });
    document.addEventListener("keydown", onKeydown);

    // ----- Triggers -----
    if (trigger === "timer" || trigger === "both") {
      if (delay <= 0) {
        // Race-avoid: if delay is 0 the popup would flash on every
        // page load. Defer to next idle moment so the rest of the
        // page paints first.
        if (window.requestIdleCallback) {
          requestIdleCallback(open, { timeout: 1500 });
        } else {
          setTimeout(open, 200);
        }
      } else {
        setTimeout(open, delay);
      }
    }

    if (trigger === "exit" || trigger === "both") {
      // Exit-intent: bind mouseleave on the documentElement. Filter
      // to mouse moving toward the top of the viewport (clientY near
      // 0 or negative). mouseleave on documentElement does NOT fire
      // when the cursor moves between sibling elements, only when it
      // genuinely leaves the document.
      var exitArmed = true;
      var exitTimer = null;
      document.documentElement.addEventListener(
        "mouseenter",
        function () {
          // Re-arm once the cursor is back inside, but only after a
          // brief settle so flicker doesn't rearm-and-trigger.
          if (exitTimer) clearTimeout(exitTimer);
          exitTimer = setTimeout(function () {
            exitArmed = true;
          }, 800);
        }
      );
      document.documentElement.addEventListener(
        "mouseleave",
        function (e) {
          if (!exitArmed) return;
          if (e.clientY > 0 && e.relatedTarget !== null) return;
          exitArmed = false;
          open();
        }
      );
    }

    handleSuccessRedirectIfNeeded(popup, successRedirect, discountCode);
  }

  function handleSuccessRedirectIfNeeded(popup, successRedirect, discountCode) {
    if (!successRedirect) return;
    var successEl = popup.querySelector(".signup-form__success");
    if (!successEl) return;
    // The form rendered with its success state (the page reloaded
    // after submission). Push the customer to /cart with the
    // discount pre-applied.
    var url = new URL(window.location.origin + "/cart");
    url.searchParams.set("discount", discountCode);
    window.location.replace(url.toString());
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", bootstrap);
  } else {
    bootstrap();
  }
})();
